import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave, faUniversity, faExchangeAlt,
  faCheckCircle, faExclamationTriangle, faSyncAlt,
  faArrowRight, faArrowLeft, faEraser
} from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// ============================================================
// 1. UTILITAIRE FETCH JSON SÉCURISÉ
// ============================================================
const fetchJSON = async (url, options = {}) => {
  const response = await fetch(url, options);
  const text = await response.text();

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} : ${text || 'Erreur inconnue'}`);
  }

  try {
    return JSON.parse(text);
  } catch (parseError) {
    throw new Error(`Réponse JSON invalide : ${text.substring(0, 200) || '(réponse vide)'}`);
  }
};

// ============================================================
// 2. CACHE DEXIE (24h) avec fallback périmé
// ============================================================
const db = new Dexie('CaisseBanqueCache');
db.version(1).stores({ soldes: 'key, data, timestamp' });
const CACHE_DURATION = 24 * 60 * 60 * 1000;

async function getCachedSoldes(key, allowExpired = false) {
  try {
    const entry = await db.soldes.get(key);
    if (!entry) return null;
    const age = Date.now() - entry.timestamp;
    if (!allowExpired && age > CACHE_DURATION) return null;
    return entry.data;
  } catch {
    return null;
  }
}

async function setCachedSoldes(key, data) {
  await db.soldes.put({ key, data, timestamp: Date.now() });
}

async function getStaleSoldes(key) {
  return getCachedSoldes(key, true);
}

// ============================================================
// 3. DÉDOUBLONNAGE DES REQUÊTES EN COURS (Map globale)
// ============================================================
const pendingFetches = new Map();

// ============================================================
// 4. CONFIGURATION API
// ============================================================
const API_URL = `${API_CONFIG2.apiUrl}transfert/api_transfert.php`;
 
// ============================================================
// 5. COMPOSANT PRINCIPAL
// ============================================================
const CaisseBanque = ({ userId }) => {
  // --- États ---
  const [soldeCaisse, setSoldeCaisse] = useState(0);
  const [banques, setBanques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [transfertLoading, setTransfertLoading] = useState(false);
  const [dataSource, setDataSource] = useState('cache');
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });

  const [direction, setDirection] = useState('caisse_to_bank');
  const [formData, setFormData] = useState({
    banqueId: '',
    montant: '',
    description: ''
  });

  // --- Refs pour annulation et timeouts ---
  const abortControllerRef = useRef(null);
  const toastTimeoutRef = useRef(null);
  const isMounted = useRef(true);

  // ============================================================
  // 6. GESTION DES TOASTS
  // ============================================================
  const showToast = useCallback((message, variant = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, variant });
    toastTimeoutRef.current = setTimeout(() => {
      if (isMounted.current) setToast({ show: false });
      toastTimeoutRef.current = null;
    }, 5000);
  }, []);

  // ============================================================
  // 7. RÉCUPÉRATION DES SOLDES (avec dédoublonnage, annulation, cache)
  // ============================================================
  const fetchSoldes = useCallback(async (forceRefresh = false, retryCount = 0) => {
    // Annuler la requête précédente
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const cacheKey = userId ? `soldes_${userId}` : 'soldes_global';
    const requestKey = forceRefresh ? `${cacheKey}_force` : cacheKey;

    // Dédoublonnage : si une requête identique est déjà en cours, on attend sa promesse
    if (pendingFetches.has(requestKey) && !forceRefresh) {
      try {
        const result = await pendingFetches.get(requestKey);
        if (isMounted.current && result) {
          setSoldeCaisse(result.soldeCaisse ?? 0);
          setBanques(result.banques ?? []);
          setDataSource('cache (partagé)');
          setLoading(false);
          setRefreshing(false);
        }
        return;
      } catch {
        pendingFetches.delete(requestKey);
      }
    }

    // Limiter les tentatives (éviter boucle infinie)
    if (retryCount > 5) {
      if (isMounted.current) {
        setLoading(false);
        setRefreshing(false);
        showToast('Erreur : trop de tentatives. Veuillez réessayer plus tard.', 'danger');
      }
      return;
    }

    // Définir l'état de chargement
    if (forceRefresh) setRefreshing(true);
    else setLoading(true);
    setDataSource('');

    const fetchPromise = (async () => {
      try {
        // 1. Cache si non forcé
        if (!forceRefresh) {
          const cached = await getCachedSoldes(cacheKey);
          if (cached) {
            if (isMounted.current) {
              setSoldeCaisse(cached.soldeCaisse ?? 0);
              setBanques(cached.banques ?? []);
              setDataSource('cache');
              setLoading(false);
              setRefreshing(false);
            }
            return cached;
          }
        }

        // 2. Requête réseau
        const uniqueParam = new Date().getTime();
        const response = await fetch(`${API_URL}?action=get_soldes&unique=${uniqueParam}`, {
          signal: controller.signal,
        });

        // 3. Gestion 429
        if (response.status === 429) {
          // Essayer le cache périmé
          const stale = await getStaleSoldes(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setSoldeCaisse(stale.soldeCaisse ?? 0);
              setBanques(stale.banques ?? []);
              setDataSource('cache (périmé)');
              showToast('429 – Utilisation du cache (données peut-être obsolètes)', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          // Sinon, backoff exponentiel
          const waitTime = Math.min(1000 * Math.pow(2, retryCount), 30000);
          showToast(`Trop de requêtes. Nouvel essai dans ${waitTime/1000}s`, 'warning');
          await new Promise(resolve => setTimeout(resolve, waitTime));
          return fetchSoldes(forceRefresh, retryCount + 1);
        }

        // 4. Gestion des autres erreurs HTTP
        if (!response.ok) {
          const stale = await getStaleSoldes(cacheKey);
          if (stale) {
            if (isMounted.current) {
              setSoldeCaisse(stale.soldeCaisse ?? 0);
              setBanques(stale.banques ?? []);
              setDataSource('cache (périmé)');
              showToast('Erreur réseau – Utilisation du cache', 'warning');
              setLoading(false);
              setRefreshing(false);
            }
            return stale;
          }
          throw new Error(`HTTP ${response.status}`);
        }

        // 5. Lecture JSON
        const data = await response.json();
        if (data.status === 'success') {
          const soldes = {
            soldeCaisse: data.soldeCaisse ?? 0,
            banques: data.banques ?? []
          };
          if (isMounted.current) {
            setSoldeCaisse(soldes.soldeCaisse);
            setBanques(soldes.banques);
            const source = forceRefresh ? 'live (forcé)' : 'live';
            setDataSource(source);
            await setCachedSoldes(cacheKey, soldes);
            if (forceRefresh) {
              showToast('Données actualisées', 'success');
            }
            setLoading(false);
            setRefreshing(false);
          }
          return soldes;
        } else {
          throw new Error(data.message || 'Erreur serveur');
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          // Requête annulée volontairement, on ne fait rien
          return null;
        }
        // Dernier recours : cache périmé
        const stale = await getStaleSoldes(cacheKey);
        if (stale && isMounted.current) {
          setSoldeCaisse(stale.soldeCaisse ?? 0);
          setBanques(stale.banques ?? []);
          setDataSource('cache (périmé)');
          showToast('Erreur – Utilisation du cache', 'warning');
          setLoading(false);
          setRefreshing(false);
          return stale;
        }
        if (isMounted.current) {
          showToast(err.message || 'Erreur de chargement', 'danger');
          setLoading(false);
          setRefreshing(false);
        }
        throw err;
      }
    })();

    // Stocker la promesse dans la Map pour dédoublonner
    pendingFetches.set(requestKey, fetchPromise);
    try {
      const result = await fetchPromise;
      return result;
    } finally {
      pendingFetches.delete(requestKey);
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [userId, showToast]);

  // ============================================================
  // 8. EFFETS : INITIALISATION ET NETTOYAGE
  // ============================================================
  useEffect(() => {
    isMounted.current = true;
    fetchSoldes();
    return () => {
      isMounted.current = false;
      if (abortControllerRef.current) abortControllerRef.current.abort();
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      pendingFetches.clear(); // Nettoyer pour éviter les fuites
    };
  }, [fetchSoldes]);

  // ============================================================
  // 9. GESTION DU FORMULAIRE
  // ============================================================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const montant = parseFloat(formData.montant);
    if (isNaN(montant) || montant <= 0) {
      showToast('Montant invalide', 'danger');
      return;
    }
    setTransfertLoading(true);
    try {
      const action = direction === 'caisse_to_bank' ? 'transfert_caisse_banque' : 'transfert_banque_caisse';
      const payload = {
        banqueId: formData.banqueId,
        montant,
        description: formData.description || `Transfert ${direction === 'caisse_to_bank' ? 'Caisse → Banque' : 'Banque → Caisse'}`,
        userId
      };
      const data = await fetchJSON(`${API_URL}?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (data.status === 'success') {
        showToast(data.message, 'success');
        setSoldeCaisse(data.nouveauSoldeCaisse ?? 0);
        setBanques(prev => prev.map(b =>
          b.id === formData.banqueId ? { ...b, solde: data.nouveauSoldeBanque ?? 0 } : b
        ));
        setFormData({ banqueId: '', montant: '', description: '' });
        // Invalider le cache en le supprimant
        const cacheKey = userId ? `soldes_${userId}` : 'soldes_global';
        await db.soldes.delete(cacheKey);
        // Forcer un rechargement frais
        fetchSoldes(true);
      } else {
        showToast(data.message || 'Erreur', 'danger');
      }
    } catch (error) {
      console.error('handleSubmit error:', error);
      showToast(error.message || 'Erreur serveur', 'danger');
    } finally {
      setTransfertLoading(false);
    }
  };

  const handleRefresh = () => {
    if (!refreshing && !loading) {
      fetchSoldes(true);
    }
  };

  // ============================================================
  // 10. VIDER LE CACHE ET FORCER LE RECHARGEMENT
  // ============================================================
  const handleClearCache = useCallback(async () => {
    if (refreshing || loading || transfertLoading) return;
    try {
      const cacheKey = userId ? `soldes_${userId}` : 'soldes_global';
      await db.soldes.delete(cacheKey);
      showToast('Cache vidé avec succès', 'success');
      // Forcer le rechargement des données
      await fetchSoldes(true);
    } catch (err) {
      showToast('Erreur lors du vidage du cache', 'danger');
    }
  }, [userId, refreshing, loading, transfertLoading, fetchSoldes, showToast]);

  // ============================================================
  // 11. AFFICHAGE DU CHARGEMENT
  // ============================================================
  if (loading && !refreshing) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="text-secondary mt-2">Chargement des soldes...</p>
      </div>
    );
  }

  // ============================================================
  // 12. RENDU PRINCIPAL (style dark blue glassmorphism)
  // ============================================================
  return (
    <>
      <style>{`
        :root {
          --accent-blue: #3b82f6;
          --accent-dark-blue: #1d4ed8;
          --accent-blue-dim: rgba(59, 130, 246, 0.10);
          --accent-blue-glow: rgba(59, 130, 246, 0.25);
          --bg-primary: #0b1428;
          --bg-gradient: radial-gradient(circle at 20% 30%, #132044, #0a0f1f);
          --card-bg: rgba(20, 40, 80, 0.55);
          --card-border: rgba(59, 130, 246, 0.15);
          --text-primary: #e0e7ff;
          --text-secondary: #94a3b8;
          --text-muted: #64748b;
          --radius: 16px;
          --shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
        }

        .caisse-banque-wrapper {
          background: var(--bg-gradient);
          min-height: 100vh;
          padding: 30px;
          color: var(--text-primary);
          font-family: 'Inter', sans-serif;
        }

        .glass-card {
          background: var(--card-bg);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
          border: 1px solid var(--card-border);
          border-radius: var(--radius);
          padding: 1.8rem;
          box-shadow: var(--shadow);
          transition: all 0.2s ease;
          margin-bottom: 24px;
        }
        .glass-card:hover {
          border-color: rgba(59, 130, 246, 0.25);
          box-shadow: 0 12px 40px rgba(59, 130, 246, 0.05);
        }

        .badge-source {
          font-size: 11px;
          padding: 4px 12px;
          border-radius: 40px;
          background: rgba(59,130,246,0.15);
          color: var(--accent-blue);
          border: 1px solid rgba(59,130,246,0.12);
          white-space: nowrap;
        }
        .badge-source.live {
          background: rgba(46,204,113,0.15);
          color: #2ecc71;
          border-color: rgba(46,204,113,0.12);
        }
        .badge-source.warning {
          background: rgba(245,158,11,0.15);
          color: #f59e0b;
          border-color: rgba(245,158,11,0.12);
        }

        .soldes-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        .solde-item {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.03);
          border-radius: 20px;
          padding: 1.5rem;
          text-align: center;
        }
        .solde-valeur {
          font-size: 2.2rem;
          font-weight: 700;
          color: var(--accent-blue);
          margin-top: 8px;
        }
        .solde-label {
          color: var(--text-secondary);
          font-size: 0.9rem;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .banque-item {
          background: rgba(255,255,255,0.02);
          border: 1px solid rgba(255,255,255,0.03);
          border-radius: 16px;
          padding: 1rem;
          margin-bottom: 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .banque-item .nom {
          font-weight: 600;
        }
        .banque-item .solde {
          color: var(--accent-blue);
          font-weight: 600;
        }

        .monetra-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 14px;
          color: var(--text-primary);
          padding: 12px;
          width: 100%;
          transition: 0.3s;
        }
        .monetra-input:focus {
          border-color: var(--accent-blue);
          outline: none;
          background: rgba(255,255,255,0.06);
          box-shadow: 0 0 0 3px rgba(59,130,246,0.08);
        }
        .monetra-input option {
          background: #0b1428;
          color: var(--text-primary);
        }

        .direction-selector {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
        }
        .direction-selector .btn-direction {
          flex: 1;
          padding: 12px;
          border-radius: 14px;
          border: 1px solid rgba(255,255,255,0.06);
          background: rgba(255,255,255,0.02);
          color: var(--text-secondary);
          text-align: center;
          cursor: pointer;
          transition: 0.3s;
        }
        .direction-selector .btn-direction.active {
          border-color: var(--accent-blue);
          background: rgba(59,130,246,0.08);
          color: var(--accent-blue);
          box-shadow: 0 0 0 1px var(--accent-blue);
        }

        .monetra-btn {
          padding: 10px 24px;
          border-radius: 30px;
          background: var(--accent-blue);
          color: #fff;
          border: none;
          font-weight: 600;
          transition: 0.3s;
          box-shadow: 0 6px 20px var(--accent-blue-glow);
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .monetra-btn:hover:not(:disabled) {
          background: var(--accent-dark-blue);
          transform: translateY(-2px);
          box-shadow: 0 8px 28px rgba(59,130,246,0.4);
        }
        .monetra-btn:disabled {
          background: rgba(255,255,255,0.06);
          color: var(--text-muted);
          box-shadow: none;
          cursor: not-allowed;
        }
        .monetra-btn.outline {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.06);
          color: var(--text-primary);
          box-shadow: none;
        }
        .monetra-btn.outline:hover {
          background: rgba(255,255,255,0.04);
        }
        .monetra-btn.danger {
          background: transparent;
          border: 1px solid rgba(239,68,68,0.3);
          color: #f87171;
          box-shadow: none;
        }
        .monetra-btn.danger:hover:not(:disabled) {
          background: rgba(239,68,68,0.08);
          border-color: #ef4444;
        }

        .toast-custom {
          background: rgba(10, 20, 45, 0.8) !important;
          backdrop-filter: blur(24px) !important;
          border: 1px solid rgba(59, 130, 246, 0.2) !important;
          border-radius: 16px !important;
          color: var(--text-primary) !important;
          box-shadow: 0 20px 50px rgba(0,0,0,0.6) !important;
        }

        .btn-close-custom {
          background: transparent;
          border: none;
          color: var(--text-secondary);
          opacity: 0.5;
          font-size: 1.2rem;
          cursor: pointer;
        }
        .btn-close-custom:hover {
          opacity: 1;
        }

        .d-flex { display: flex; }
        .gap-2 { gap: 8px; }
        .gap-3 { gap: 16px; }
        .fw-bold { font-weight: 600; }
        .text-secondary { color: var(--text-secondary); }
        .text-muted { color: var(--text-muted); }
        .text-success { color: var(--accent-blue); }
        .text-danger { color: #ef4444; }
        .mb-4 { margin-bottom: 1.5rem; }
        .mb-3 { margin-bottom: 1rem; }
        .mt-2 { margin-top: 0.5rem; }
        .me-2 { margin-right: 0.5rem; }
        .w-100 { width: 100%; }
        .text-center { text-align: center; }
        .py-5 { padding-top: 3rem; padding-bottom: 3rem; }
        .align-items-center { align-items: center; }
        .justify-content-between { justify-content: space-between; }
        .flex-wrap { flex-wrap: wrap; }

        @media (max-width: 768px) {
          .caisse-banque-wrapper { padding: 16px; }
          .soldes-grid { grid-template-columns: 1fr; }
          .direction-selector { flex-direction: column; }
        }
      `}</style>

      <div className="caisse-banque-wrapper">
        {/* En-tête avec badge source, refresh et vider le cache */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <h2 className="fw-bold mb-0">
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" style={{ color: 'var(--accent-blue)' }} />
            Gestion Caisse / Banque
          </h2>
          <div className="d-flex align-items-center gap-3">
            <span className={`badge-source ${dataSource === 'live' || dataSource === 'live (forcé)' ? 'live' : dataSource === 'cache (périmé)' ? 'warning' : ''}`}>
              {dataSource === 'cache' ? '📦 Cache' :
               dataSource === 'cache (périmé)' ? '⚠️ Cache périmé' :
               dataSource === 'cache (partagé)' ? '🔄 Partage' :
               dataSource === 'live' ? '🔄 Live' :
               dataSource === 'live (forcé)' ? '🔄 Forcé' : '📦 Cache'}
            </span>
            <button
              className="monetra-btn outline"
              onClick={handleRefresh}
              disabled={refreshing || loading || transfertLoading}
            >
              <FontAwesomeIcon icon={faSyncAlt} spin={refreshing} />
              {refreshing ? 'Actualisation...' : 'Actualiser'}
            </button>
            <button
              className="monetra-btn danger"
              onClick={handleClearCache}
              disabled={refreshing || loading || transfertLoading}
              title="Vider le cache et forcer le rechargement"
            >
              <FontAwesomeIcon icon={faEraser} /> Vider le cache
            </button>
          </div>
        </div>

        {/* Cartes des soldes */}
        <div className="glass-card">
          <div className="soldes-grid">
            <div className="solde-item">
              <div className="solde-label"><FontAwesomeIcon icon={faMoneyBillWave} className="me-1" /> Caisse</div>
              <div className="solde-valeur">{soldeCaisse.toFixed(2)}$</div>
            </div>
            <div className="solde-item">
              <div className="solde-label"><FontAwesomeIcon icon={faUniversity} className="me-1" /> Total Banque</div>
              <div className="solde-valeur">
                {banques.reduce((sum, b) => sum + parseFloat(b.solde || 0), 0).toFixed(2)}$</div>
            </div>
          </div>

          <div className="mt-4">
            <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)' }}>Comptes bancaires</h5>
            {banques.length === 0 ? (
              <p className="text-secondary">Aucun compte bancaire enregistré.</p>
            ) : (
              banques.map(b => (
                <div key={b.id} className="banque-item">
                  <div>
                    <div className="nom">{b.nomCompte}</div>
                    <small className="text-secondary">{b.banqueNom} • {b.numeroCompte}</small>
                  </div>
                  <div className="solde">{parseFloat(b.solde || 0).toFixed(2)}$</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Formulaire de transfert */}
        <div className="glass-card">
          <h5 className="fw-bold mb-3" style={{ color: 'var(--accent-blue)' }}>
            <FontAwesomeIcon icon={faExchangeAlt} className="me-2" />
            Transfert d'argent
          </h5>

          <div className="direction-selector">
            <div
              className={`btn-direction ${direction === 'caisse_to_bank' ? 'active' : ''}`}
              onClick={() => setDirection('caisse_to_bank')}
            >
              <FontAwesomeIcon icon={faArrowRight} className="me-2" />
              Caisse → Banque
            </div>
            <div
              className={`btn-direction ${direction === 'bank_to_caisse' ? 'active' : ''}`}
              onClick={() => setDirection('bank_to_caisse')}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Banque → Caisse
            </div>
          </div>

          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">
                {direction === 'caisse_to_bank' ? 'Compte destinataire' : 'Compte source'}
              </Form.Label>
              <Form.Select
                name="banqueId"
                value={formData.banqueId}
                onChange={handleChange}
                required
                className="monetra-input"
              >
                <option className="bg-dark" value="">-- Sélectionner un compte --</option>
                {banques.map(b => (
                  <option className="bg-dark" key={b.id} value={b.id}>
                    {b.nomCompte} ({b.banqueNom}) - Solde : {parseFloat(b.solde || 0).toFixed(2)}$
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">Montant ($)</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-transparent text-secondary border-secondary">$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  name="montant"
                  value={formData.montant}
                  onChange={handleChange}
                  required
                  className="monetra-input text-success"
                  placeholder="0.00"
                />
              </InputGroup>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="text-secondary small">Description (optionnel)</Form.Label>
              <Form.Control
                as="textarea"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="monetra-input text-success"
                placeholder="Motif du transfert"
                rows={2}
              />
            </Form.Group>

            <Button
              type="submit"
              className="monetra-btn w-100 mt-2"
              disabled={transfertLoading || !formData.banqueId || !formData.montant}
            >
              {transfertLoading ? (
                <><Spinner as="span" animation="border" size="sm" className="me-2" /> Transfert en cours...</>
              ) : (
                `Transférer (${direction === 'caisse_to_bank' ? 'Caisse → Banque' : 'Banque → Caisse'})`
              )}
            </Button>
          </Form>
        </div>
      </div>

      {/* Toast de notification */}
      {toast.show && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999 }}>
          <div className={`toast-custom p-3 ${toast.variant}`} style={{ minWidth: '300px' }}>
            <div className="d-flex align-items-center gap-3">
              <FontAwesomeIcon
                icon={toast.variant === 'success' ? faCheckCircle : faExclamationTriangle}
                className={toast.variant === 'success' ? 'text-success' : 'text-danger'}
              />
              <span>{toast.message}</span>
              <button className="btn-close-custom ms-auto" onClick={() => setToast({ ...toast, show: false })}>✕</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaisseBanque;